import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useMutation } from "@tanstack/react-query";
import { ListItem, Text } from "@ui-kitten/components";
import Button from "components/Button";
import Chevron from "components/Chevron";
import { LIGHT_BLUE } from "constants/";
import {
  BestTicketBlockDetails,
  BestTicketDetails,
  SeatDetails,
  SectionDetails,
  TicketDetails,
} from "features/types";
import { useAppSelector } from "hooks/useAppSelector";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { findSeatsBySectionAndRowRequest } from "queries/venue.queries";
import React, { useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import Collapsible from "react-native-collapsible";
import { formatCurrency } from "utils/appUtils";
import { isSectionDetails } from "../helpers";

interface Props {
  item: BestTicketBlockDetails | SectionDetails;
  selectingBestBlock: boolean;
  hideRating: boolean;
  color: string;
  rs: number;
  msg: string;
  shouldShowGuestPrice: boolean;
  includeAccessible: boolean;
  handleSectionPress: (section: SectionDetails) => void;
  handleBestBlockPress: (seats: SeatDetails[]) => void;
  originalTickets?: TicketDetails[];
}

const SECTION_ROW_SEATS_CACHE: Record<string, SeatDetails[]> = {};

const getCacheKey = (venue: string, section: string, row: string) => {
  return `${venue}-${section}-${row}`;
};

const SectionItem: React.FC<Props> = (props) => {
  const {
    item,
    selectingBestBlock,
    hideRating,
    color,
    msg,
    rs,
    shouldShowGuestPrice,
    includeAccessible,
    handleBestBlockPress,
    handleSectionPress,
    originalTickets,
  } = props;
  const { data: branding } = useFetchTenantBrandingQuery();
  const {
    selectedPartySize,
    selectedEvent,
    selectedSubscription,
    isChangingSeats,
  } = useAppSelector((state) => state.reservationFlow);

  const { data: seats, mutateAsync: findSeatsBySectionAndRow } = useMutation(
    findSeatsBySectionAndRowRequest,
  );
  const isRSVP = selectedEvent?.eventType === "RSVP";
  const allInclusivePrice =
    selectedSubscription?.planPackage.allInclusivePrice || false;
  const seatSelectionSectionLabelEnabled =
    selectedSubscription?.planPackage.seatSelectionSectionLabelEnabled ?? true;
  const [checkedSeats, setCheckedSeats] = useState<SeatDetails[]>([]);
  const [isSeatSelectorOpen, setIsSeatSelectorOpen] = useState(false);

  // Original tickets that belong to this block (section + row match)
  const originalSeatsInThisBlock = useMemo(() => {
    if (!originalTickets || isSectionDetails(item)) return [];
    const block = item as BestTicketBlockDetails;
    return originalTickets.filter(
      (t) => t.seat.section === block.section && t.seat.row === block.row,
    );
  }, [originalTickets, item]);

  const originalSeatIdSet = useMemo(
    () => new Set(originalSeatsInThisBlock.map((t) => t.seat.id)),
    [originalSeatsInThisBlock],
  );
  const originalCount = originalSeatsInThisBlock.length;
  const isSeatOriginal = (seat: SeatDetails) => originalSeatIdSet.has(seat.id);
  // Original seats are selectable when party size >= original count
  const isOriginalSelectable =
    originalCount > 0 && selectedPartySize >= originalCount;

  // True when this item (section or block) contains the user's current reservation
  const isCurrentReservationItem = useMemo(() => {
    if (!originalTickets || originalTickets.length === 0) return false;
    if (isSectionDetails(item)) {
      // Step 1 sections view: highlight by section only
      return originalTickets.some((t) => t.seat.section === item.section);
    }
    // Step 2 blocks view: highlight by section + row (same as originalCount check)
    return originalCount > 0;
  }, [originalTickets, item, originalCount]);

  const getPrice = (
    item: SectionDetails | BestTicketBlockDetails,
    baseChargeSelector: (
      item: SectionDetails | BestTicketBlockDetails,
    ) => string,
    feesSelector: (item: SectionDetails | BestTicketBlockDetails) => string,
    allInclusivePrice: boolean,
  ): number => {
    return (
      Number(baseChargeSelector(item)) +
      (allInclusivePrice ? Number(feesSelector(item)) : 0)
    );
  };

  const getTicketPrice = (item: SectionDetails | BestTicketBlockDetails) => {
    // If self request comp ticket is enabled, show $0
    if (selectedSubscription?.planPackage?.userSelfRequestCompTicketsEnabled) {
      return 0;
    }

    return getPrice(
      item,
      (item) =>
        isSectionDetails(item)
          ? item.minPlanTicketCharge || ""
          : item.perTicketCharge || "",
      (item) =>
        isSectionDetails(item)
          ? item.minPlanTicketFees || ""
          : item.perTicketFees || "",
      allInclusivePrice,
    );
  };

  const getGuestTicketPrice = (
    item: SectionDetails | BestTicketBlockDetails,
  ) => {
    return getPrice(
      item,
      (item) =>
        isSectionDetails(item)
          ? item.minPlanGuestCharge || ""
          : item.perGuestCharge || "",
      (item) =>
        isSectionDetails(item)
          ? item.minPlanGuestFees || ""
          : item.perGuestFees || "",
      allInclusivePrice,
    );
  };

  const shouldShowRating = useMemo(() => {
    return !item.ga && !hideRating && selectingBestBlock;
  }, [item, hideRating, selectingBestBlock]);

  const renderTitle = useMemo(
    () => (
      <View className="flex flex-row justify-between items-center">
        <View className="column basis-2/3">
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Text style={styles.sectionTextStyle}>
              {seatSelectionSectionLabelEnabled ? "Section " : ""}
              {item.sectionDisplay}{" "}
              {
                // if selectingBestBlock is true, then show the rowDisplay. Don't show row for RSVP events.
                (selectingBestBlock
                  ? item.ga || selectedEvent?.eventType === "RSVP"
                    ? ""
                    : `Row ${(item as BestTicketBlockDetails).rowDisplay} `
                  : null) as string
              }
              {
                // if item.accessible is true and includeAccessible is true, then show the wheelchair icon
                (item.accessible && includeAccessible && (
                  <MaterialCommunityIcons
                    name="wheelchair-accessibility"
                    size={20}
                    color={LIGHT_BLUE}
                  />
                )) as React.ReactElement
              }
            </Text>
            {isCurrentReservationItem && (
              <View
                testID="current-reservation-badge"
                style={{
                  backgroundColor: "#3498db",
                  borderRadius: 4,
                  paddingHorizontal: 5,
                  paddingVertical: 2,
                  marginLeft: 4,
                }}
              >
                <Text style={{ color: "white", fontSize: 11 }}>★ Current</Text>
              </View>
            )}
          </View>
          {shouldShowRating && (
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <View
                style={{
                  marginRight: 5,
                  borderRadius: 6,
                  paddingHorizontal: 6,
                  backgroundColor: color,
                  paddingVertical: 5,
                }}
              >
                <Text style={{ color: "white" }}>{rs}</Text>
              </View>
              <Text>{msg}</Text>
            </View>
          )}
        </View>

        {!isRSVP && (
          <View>
            <Text style={{ fontSize: 14, fontWeight: "700" }}>
              {`${formatCurrency(getTicketPrice(item), {
                priceType: item.priceType,
              })}`}
              {shouldShowGuestPrice
                ? ` / ${formatCurrency(getGuestTicketPrice(item), {
                    priceType: item.priceType,
                  })}`
                : ""}
            </Text>
          </View>
        )}
      </View>
    ),
    [item, selectingBestBlock, includeAccessible, hideRating, color, rs, msg],
  );

  const hasCachedSeats = (venue: string = "", section: string, row: string) => {
    const cachedKey = getCacheKey(venue, section, row);
    return SECTION_ROW_SEATS_CACHE[cachedKey] !== undefined;
  };

  const setCachedSeats = (
    venue: string = "",
    section: string,
    row: string,
    seats: SeatDetails[],
  ) => {
    const cachedKey = getCacheKey(venue, section, row);
    // If seats are flipped, reverse them - https://github.com/FanRally/fanrally-core/issues/1591
    if (seats.some((seat) => seat.flipped)) {
      SECTION_ROW_SEATS_CACHE[cachedKey] = seats.reverse();
    } else {
      SECTION_ROW_SEATS_CACHE[cachedKey] = seats;
    }
  };

  const getCacheSeats = (venue: string = "", section: string, row: string) => {
    const cachedKey = getCacheKey(venue, section, row);
    return SECTION_ROW_SEATS_CACHE[cachedKey];
  };

  // if the section and row is not cached, then fetch the allSeatsInRow
  const handleMenuGroupPress = async () => {
    const section = item.section;
    const row = (item as BestTicketBlockDetails).row;
    if (hasCachedSeats(selectedEvent?.venueName, section, row)) {
      return;
    }
    const seatsForRow = await findSeatsBySectionAndRow({
      venueId: selectedEvent!.venueId,
      section,
      row,
      eventTypeName: selectedEvent!.eventType,
    });
    setCachedSeats(selectedEvent?.venueName, section, row, seatsForRow);
  };

  const handleToggleSeatSelector = async () => {
    // Toggle visibility; on opening, ensure seats are fetched/cached
    const willOpen = !isSeatSelectorOpen;
    setIsSeatSelectorOpen(willOpen);
    if (willOpen) {
      await handleMenuGroupPress();
    }
  };

  const cachedSeats = getCacheSeats(
    selectedEvent?.venueName,
    item.section,
    (item as BestTicketBlockDetails).row,
  );

  const sortedSeats = useMemo(() => {
    const seats = [...checkedSeats];
    // If seats are flipped, reverse them - https://github.com/FanRally/fanrally-core/issues/1591
    if (seats.some((seat) => seat.flipped)) {
      return seats.sort((a, b) =>
        b.seatDisplay.localeCompare(a.seatDisplay, "en", { numeric: true }),
      );
    }
    return seats.sort((a, b) =>
      a.seatDisplay.localeCompare(b.seatDisplay, "en", { numeric: true }),
    );
  }, [checkedSeats]);

  const isSeatChecked = (seat: SeatDetails) =>
    checkedSeats.some((checkedSeat) => checkedSeat.id === seat.id);

  // Check if the seat is available in the best block
  const isSeatAvailable = (seat: SeatDetails) => {
    const seatAvailable = (item as BestTicketBlockDetails).tickets.some(
      (ticket) => ticket.seatId === seat.id,
    );
    return seatAvailable;
  };

  // Get the ticket status for a given seat in this best block (e.g., REQUESTED)
  const isSeatRequestedByAnother = (seat: SeatDetails) => {
    const ticket = (item as BestTicketBlockDetails).tickets.find(
      (t) => t.seatId === seat.id,
    );
    return ticket?.requestedByAnother;
  };

  // If checkedSeats.length is less than selectedPartySize, then add the seat to checkedSeats
  // If checkedSeats.length is equal to selectedPartySize, then remove the first non-original seat from checkedSeats and add the new seat
  const handleCheckSeat = (seat: SeatDetails) => {
    if (!isSeatAvailable(seat) && !isSeatOriginal(seat)) return;
    if (isSeatOriginal(seat) && !isOriginalSelectable) return;
    if (checkedSeats.length < selectedPartySize) {
      setCheckedSeats([...checkedSeats, seat]);
    } else {
      // When at capacity, prefer to remove the first non-original seat
      const firstNonOriginalIndex = checkedSeats.findIndex(
        (s) => !isSeatOriginal(s),
      );
      if (firstNonOriginalIndex !== -1) {
        const updated = [...checkedSeats];
        updated.splice(firstNonOriginalIndex, 1);
        setCheckedSeats([...updated, seat]);
      } else {
        setCheckedSeats([...checkedSeats.slice(1), seat]);
      }
    }
  };

  const handleRemoveSeat = (seat: SeatDetails) => {
    setCheckedSeats(
      checkedSeats.filter((checkedSeat) => checkedSeat.id !== seat.id),
    );
  };

  // Check if the seat is unavailable meaning it is not available or it is already checked
  const isSeatCheckedOrUnavailable = (seat: SeatDetails) => {
    return isSeatChecked(seat) || !isSeatAvailable(seat);
  };

  const isSeatUncheckedAndAvailable = (seat: SeatDetails) => {
    return !isSeatChecked(seat) && isSeatAvailable(seat);
  };

  /**
   * Determines whether the available seat at index i is a “dangling single”.
   * A seat is considered a dangling single when:
   * Interior case: it is 'A' and both neighbors block selection ('C' or 'U'),
   * and at least one neighbor is a selected seat ('C').
   * Edge case: it is 'A' at the boundary next to a selected seat ('C'),
   * but only when more than one 'A' remains in the row.
   * Boundaries outside the array are treated as 'B' (boundary).
   * State legend: 'C' = checked, 'A' = available (unchecked), 'U' = unavailable.
   * @param state Array of seat states for the row ('C' | 'A' | 'U').
   * @param i Index of the seat to evaluate.
   * @param onlyOneARemains True if exactly one 'A' remains in the entire row.
   * @returns True if the seat at index i is a dangling single; otherwise false.
   * @example
   * // state: [ 'C', 'A', 'U' ] → interior single at index 1
   * isDanglingSingleAt(['C','A','U'], 1, false) // true
   * @example
   * // state: [ 'A', 'C' ] with multiple 'A' remaining → edge single at index 0
   * isDanglingSingleAt(['A','C'], 0, false) // true
   * @example
   * // state: [ 'A', 'C' ] with only one 'A' remaining → not flagged
   * isDanglingSingleAt(['A','C'], 0, true) // false
   */
  const hasDanglingSingles = useMemo(() => {
    if (!cachedSeats) return false;
    if (checkedSeats.length === 0) return false;
    if (checkedSeats.length < selectedPartySize) return false;

    const checkedIds = new Set(checkedSeats.map((s) => s.id));
    const isAvail = (seat: SeatDetails) =>
      isSeatAvailable(seat) || (isSeatOriginal(seat) && isOriginalSelectable);

    // Build seat state: C = checked, A = available (unchecked), U = unavailable
    const state = cachedSeats.map((seat) => {
      if (checkedIds.has(seat.id)) return "C";
      return isAvail(seat) ? "A" : "U";
    });

    // Remaining available seats (unchecked)
    const remainingA = state.reduce((n, s) => n + (s === "A" ? 1 : 0), 0);
    const onlyOneARemains = remainingA === 1;

    const at = (i: number) => (i < 0 || i >= state.length ? "B" : state[i]); // B = boundary
    const isBlocking = (s: string) => s === "C" || s === "U"; // blocks extending the group

    const isSelectedState = (s: string) => s === "C";
    for (let i = 0; i < state.length; i++) {
      if (state[i] !== "A") continue;

      const left = at(i - 1);
      const right = at(i + 1);

      const leftBlocks = isBlocking(left);
      const rightBlocks = isBlocking(right);
      const bothNeighborsSelected =
        isSelectedState(left) && isSelectedState(right);

      // Interior single only when trapped between two selected seats (C A C)
      if (leftBlocks && rightBlocks && bothNeighborsSelected) return true;

      // Edge single next to selection is only flagged when there’s more than one A remaining
      if (!onlyOneARemains) {
        if (
          (left === "B" && isSelectedState(right)) ||
          (right === "B" && isSelectedState(left))
        ) {
          return true;
        }
      }
    }

    return false;
  }, [cachedSeats, checkedSeats, selectedPartySize]);

  const renderLegendIcon = (seat: SeatDetails) => {
    if (seat.accessible) {
      return (
        <MaterialCommunityIcons name="wheelchair-accessibility" color="white" />
      );
    }
    if (seat.aisle) {
      return <Text status="control">A</Text>;
    }
  };

  // Only show the individual seat selector if it is not a GA section, not an RSVP event, and the provider is not TFL
  const shouldShowSeatSelector =
    !item.ga &&
    selectedEvent?.eventType !== "RSVP" &&
    selectingBestBlock &&
    (item as BestTicketBlockDetails).tickets[0].provider !== "TFL";

  const hasRequestedTickets = useMemo(() => {
    if (!selectingBestBlock) return false;
    const tickets = (item as BestTicketBlockDetails).tickets;
    return tickets?.some((t) => t.requestedByAnother) || false;
  }, [item, selectingBestBlock]);

  const renderSeatSelector = () => {
    return (
      <View testID="seat-selector">
        <View>
          <Text style={{ fontWeight: "700" }}>
            Selected seats:{" "}
            {sortedSeats
              .map((seat) => {
                if (seat.aisle) {
                  return seat.seatDisplay + " (aisle)";
                }
                return seat.seatDisplay;
              })
              .join(", ")}
          </Text>
          <ScrollView horizontal className="mt-2">
            {cachedSeats ? (
              <>
                {cachedSeats.map((seat) =>
                  isSeatChecked(seat) ? (
                    <Pressable
                      key={seat.id}
                      className="h-5 w-5 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: isSeatOriginal(seat)
                          ? "#e67e22" // amber — current seat, selected
                          : "#69b34c", // green — new seat, selected
                      }}
                      onPress={() => handleRemoveSeat(seat)}
                    >
                      <MaterialCommunityIcons
                        name="check"
                        size={15}
                        color="white"
                      />
                    </Pressable>
                  ) : isSeatOriginal(seat) ? (
                    // Current seat, not selected — shown in blue, only pressable if selectable
                    <Pressable
                      key={seat.id}
                      onPress={() => handleCheckSeat(seat)}
                      style={{ backgroundColor: "#3498db" }}
                      className="h-5 w-5 rounded-full justify-center items-center"
                      testID="current-reservation-seat"
                    >
                      {renderLegendIcon(seat)}
                    </Pressable>
                  ) : (
                    <Pressable
                      key={seat.id}
                      onPress={() => handleCheckSeat(seat)}
                      style={{
                        backgroundColor: isSeatAvailable(seat)
                          ? isSeatRequestedByAnother(seat)
                            ? "#f0ad4e" // requested tickets shown in orange
                            : branding?.primaryColor
                          : "#ebebeb",
                      }}
                      className="h-5 w-5 rounded-full justify-center items-center"
                      testID={isSeatAvailable(seat) ? "available-seat" : undefined}
                    >
                      {renderLegendIcon(seat)}
                    </Pressable>
                  ),
                )}
              </>
            ) : (
              // placeholder
              <View className="h-5 w-5"></View>
            )}
          </ScrollView>
          <View className="flex flex-row flex-wrap items-center mt-1.5">
            <View className="flex flex-row items-center mr-2 mb-1">
              <View className="h-4 w-4 rounded-full mr-1.5 bg-[#69b34c]" />
              <Text category="c1">Selected</Text>
            </View>

            {originalCount > 0 && !isSectionDetails(item) && (
              <>
                <View className="flex flex-row items-center mr-2 mb-1">
                  <View className="h-4 w-4 rounded-full mr-1.5 bg-[#e67e22]" />
                  <Text category="c1">Current Seat (Selected)</Text>
                </View>
                <View className="flex flex-row items-center mr-2 mb-1">
                  <View className="h-4 w-4 rounded-full mr-1.5 bg-[#3498db]" />
                  <Text category="c1">Current Seat</Text>
                </View>
              </>
            )}

            <View className="flex flex-row items-center mr-2 mb-1">
              <View
                className="h-4 w-4 rounded-full mr-1.5"
                style={{ backgroundColor: branding?.primaryColor || "#3b82f6" }}
              />
              <Text category="c1">Available</Text>
            </View>

            {hasRequestedTickets && (
              <View className="flex flex-row items-center mr-2 mb-1">
                <View className="h-4 w-4 rounded-full mr-1.5 bg-[#f0ad4e]" />
                <Text category="c1">Requested</Text>
              </View>
            )}

            <View className="flex flex-row items-center mr-2 mb-1">
              <View className="h-4 w-4 rounded-full mr-1.5 bg-[#ebebeb]" />
              <Text category="c1">Unavailable</Text>
            </View>

            <View className="flex flex-row items-center mr-2 mb-1">
              <View
                className="h-4 w-4 rounded-full mr-1.5 items-center justify-center"
                style={{ backgroundColor: branding?.primaryColor || "#3b82f6" }}
              >
                <MaterialCommunityIcons
                  name="wheelchair-accessibility"
                  size={12}
                  color="white"
                />
              </View>
              <Text category="c1">Accessible</Text>
            </View>

            <View className="flex flex-row items-center mr-2 mb-1">
              <View
                className="h-4 w-4 rounded-full mr-1.5 items-center justify-center"
                style={{ backgroundColor: branding?.primaryColor || "#3b82f6" }}
              >
                <Text className="text-[11px] leading-4" status="control">
                  A
                </Text>
              </View>
              <Text category="c1">Aisle</Text>
            </View>
          </View>
          <Text style={{ marginTop: 4 }} status="danger" category="c1">
            {hasDanglingSingles
              ? "This venue won't allow an empty seat between you and another guest. Please choose another seat."
              : ""}
          </Text>
        </View>
        <Button
          testID="reserve-seats-button"
          onPress={() => handleBestBlockPress(checkedSeats)}
          disabled={
            checkedSeats.length !== selectedPartySize ||
            hasDanglingSingles ||
            (isChangingSeats &&
              checkedSeats.length > 0 &&
              checkedSeats.every((s) => isSeatOriginal(s)))
          }
        >
          {selectedSubscription?.planPackage?.userSelfRequestEnabled
            ? "Request"
            : "Reserve"}
        </Button>
      </View>
    );
  };
  const toSeatDetails = (ticket: BestTicketDetails): SeatDetails =>
    ({
      id: ticket.seatId,
      section: ticket.section,
      row: ticket.row,
      seat: ticket.seat,
      seatDisplay: ticket.seatDisplay,
      aisle: ticket.aisle,
      flipped: ticket.flipped,
    }) as SeatDetails;

  // Automatically select seats on load, incorporating original ticket logic
  useEffect(() => {
    if (!selectingBestBlock) return;

    const block = item as BestTicketBlockDetails;

    // No original tickets for this block — standard auto-select first N available
    if (originalCount === 0) {
      const preSelectedSeats: SeatDetails[] = [];
      for (let i = 0; i < selectedPartySize; i++) {
        const seat = block.tickets[i];
        if (!seat) break;
        preSelectedSeats.push(toSeatDetails(seat));
      }
      setCheckedSeats(preSelectedSeats);
      return;
    }

    // Fewer seats than original — no pre-selection, user picks non-original seats
    if (selectedPartySize < originalCount) {
      setCheckedSeats([]);
      return;
    }

    // Same count — pre-select the original seats
    if (selectedPartySize === originalCount) {
      setCheckedSeats(originalSeatsInThisBlock.map((t) => t.seat));
      return;
    }

    // More seats than original — try to expand adjacent seats (lower direction first)
    const extraNeeded = selectedPartySize - originalCount;
    const availableSeatNums = block.tickets.map((t) => parseInt(t.seat));
    const originalSeatNums = originalSeatsInThisBlock.map((t) =>
      parseInt(t.seat.seat),
    );
    let lowerCursor = Math.min(...originalSeatNums) - 1;
    let upperCursor = Math.max(...originalSeatNums) + 1;
    const adjacentSeatDetails: SeatDetails[] = [];

    while (adjacentSeatDetails.length < extraNeeded) {
      const lowerOk = availableSeatNums.includes(lowerCursor);
      const upperOk = availableSeatNums.includes(upperCursor);
      if (!lowerOk && !upperOk) break;
      if (lowerOk) {
        const found = block.tickets.find(
          (t) => parseInt(t.seat) === lowerCursor,
        );
        if (found) adjacentSeatDetails.push(toSeatDetails(found));
        lowerCursor--;
      } else {
        const found = block.tickets.find(
          (t) => parseInt(t.seat) === upperCursor,
        );
        if (found) adjacentSeatDetails.push(toSeatDetails(found));
        upperCursor++;
      }
    }

    if (adjacentSeatDetails.length < extraNeeded) {
      // Not enough adjacent seats — show originals as "current" only, user picks freely
      setCheckedSeats([]);
    } else {
      setCheckedSeats([
        ...originalSeatsInThisBlock.map((t) => t.seat),
        ...adjacentSeatDetails,
      ]);
    }
  }, [
    selectedPartySize,
    selectingBestBlock,
    item,
    seats,
    originalSeatsInThisBlock,
  ]);

  return (
    <View>
      {selectingBestBlock ? (
        <>
          {shouldShowSeatSelector ? (
            <>
              <ListItem
                title={renderTitle}
                accessoryRight={
                  <Chevron direction={isSeatSelectorOpen ? "down" : "right"} />
                }
                onPress={handleToggleSeatSelector}
                testID="section-item-block"
              ></ListItem>
              <Collapsible collapsed={!isSeatSelectorOpen} duration={250}>
                <View className="p-4">{renderSeatSelector()}</View>
              </Collapsible>
            </>
          ) : (
            <ListItem
              title={renderTitle}
              accessoryRight={<Chevron direction="right" />}
              testID="section-item-block"
              onPress={() => handleBestBlockPress(checkedSeats)}
            ></ListItem>
          )}
        </>
      ) : (
        <ListItem
          title={renderTitle}
          accessoryRight={<Chevron direction="right" />}
          onPress={() => handleSectionPress(item as SectionDetails)}
        ></ListItem>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  sectionTextStyle: {
    fontSize: 16,
    fontWeight: "700",
  },
});
export default SectionItem;
